import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import NProgress from 'nprogress';

import { authApi } from '@/utilities/axios';
import EventBus from '../../../../../utilities/event-bus';
import { useAuthStore } from '@/modules/auth/store';
import { showToast } from '@/utilities/toast-handler';
import { formatDisplayDate } from '@/utilities/date-formatter';
import { SharedTypes } from '../../../enum';
import { UserProfileTypes, AttributeSubmissionTypes } from '../enum';
import { getAllAttributeTypes } from '../constants';

const COMPANY_ATTRIBUTE_URL = process.env.VUE_APP_API_VERSION + '/template/attributes';
const SHARED_ATTRIBUTE_URL = process.env.VUE_APP_API_VERSION + '/template/attributes/shared';
const TAGGED_ATTRIBUTE_URL = process.env.VUE_APP_API_VERSION + '/template/attributes/tags/attributes';
const SHARED_TAGGED_ATTRIBUTE_URL = process.env.VUE_APP_API_VERSION + '/attributes/shared/codes';

export const useAttributeStore = defineStore('attribute', () => {
    const authStore = useAuthStore();
    const company_id = computed(() => {
        return authStore.getLoggedUserCompanyId;
    });

    const input_types = ref(getAllAttributeTypes()); // type of attribute
    // states
    const params = ref({
        company_id: null,
        categories: [],
        code: null,
        code_exact: null,
        used: null, // add filter to handle this in AttributeFilters later
        user_profile: UserProfileTypes.NORMAL,
        default: true,
        shared: SharedTypes.COMPANY, // this filter is only for front
    });
    const attribute = ref({
        // overall attribute properties
        company_id: null,
        label: null,
        code: null,
        description: null,
        placeholder: null,
        icon: null,
        formula_id: null,
        category_id: null,
        type: null,
        required: null,
        // normal/shared attribute properties
        unit: null,
        user_profile: false,
        options: null,
        rules: null,
        // tagged attribute properties
        tags: [],
        shared: null, // shareable flag
    });
    const editId = ref(null);
    const attributes = ref([]);
    const tags = ref([]);
    const sharedTaggedAttributes = ref([]);
    const totalAttributes = ref(null);
    const loading = ref(false);

    // getters
    const allAttributes = computed(() => {
        return attributes.value?.map((attribute) => {
            return {
                ...attribute,
                type: input_types.value?.find((type) => type.value === parseInt(attribute.type))?.name,
                created_at: formatDisplayDate(attribute.created_at),
            };
        });
    });

    const allTags = computed(() => {
        return tags.value;
    });

    const allSharedTaggedAttributes = computed(() => {
        return sharedTaggedAttributes.value;
    });

    // actions
    const fetchAllAttributes = async () => {
        try {
            NProgress.start();
            loading.value = true;
            const URL = params.value?.shared === SharedTypes.COMPANY ? COMPANY_ATTRIBUTE_URL : SHARED_ATTRIBUTE_URL;
            const bodyParams = {
                ...params.value,
                company_id: params.value?.shared === SharedTypes.COMPANY ? company_id.value : null,
            };
            delete bodyParams.shared;
            const response = await authApi.get(URL, {
                params: bodyParams,
            });
            if (response.statusCode === 200) {
                attributes.value = response.data;
                totalAttributes.value = attributes.value?.length;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch all attributes');
        } finally {
            NProgress.done();
            loading.value = false;
        }
    };

    const createAttribute = async (body, submissionType) => {
        try {
            EventBus.emit('show-operation-loader');
            const URL = loadUrl(submissionType);
            const response = await authApi.post(URL, { ...body, company_id: submissionType !== AttributeSubmissionTypes.SHARED ? company_id.value : null }); // put company id incase its not shared type
            if (response.statusCode === 201) {
                // push the data into the attributes.value
                attributes.value?.push(response.data);
                showToast(response.toast, 'Attribute created successfully');
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to create attribute');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const editAttribute = (attributeId) => {
        editId.value = attributeId;
        let attributeData = attributes.value?.find((attribute) => attribute.id === attributeId);
        // check if its a tagged attribute or not before proceeding to edit
        const tags = attributeData?.tags ?? attribute.value?.tags ?? [];
        if (tags && tags.length) {
            // if yes, check if its shared or not as well
            // to do so, compare its tag_id and see if it exists in the list of sharedTaggedAttributes
            const tagId = tags[0]?.tag_id;
            const existingSharedTag = sharedTaggedAttributes.value?.find((tag) => tag.id === tagId);
            // if it exists then its shared, else its not shared
            attributeData = {
                ...attributeData,
                tags: [{ id: tagId }],
                shared: existingSharedTag ? true : false,
            };
        }
        loadAttributeDetails(attributeData);
    };

    const updateAttribute = async (body, submissionType) => {
        try {
            EventBus.emit('show-operation-loader');
            const URL = loadUrl(submissionType);
            const response = await authApi.patch(`${URL}/${editId.value}`, body);
            if (response.statusCode === 200) {
                // push the data into the attributes.value
                showToast(response.toast, 'Attribute updated successfully');
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to update attribute');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const deleteAttribute = async (attributeId, submissionType) => {
        try {
            EventBus.emit('show-operation-loader');
            // incase submissionType is not present or null
            if (!submissionType) {
                submissionType = getSubmissionTypeByAttributeId(attributeId);
            }
            const URL = loadUrl(submissionType);
            const response = await authApi.delete(`${URL}/${attributeId}`, {
                params: {
                    company_id: submissionType !== AttributeSubmissionTypes.SHARED ? company_id.value : null,
                },
            });
            if (response.statusCode === 200) {
                showToast(response.toast, 'Attribute deleted successfully');
                // remove it from the array after deletion
                attributes.value = attributes.value?.filter((attribute) => attribute.id !== attributeId);
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to delete attribute');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const setDefaultAttribute = async (attributeId, submissionType) => {
        try {
            EventBus.emit('show-operation-loader');
            if (!submissionType) {
                submissionType = getSubmissionTypeByAttributeId(attributeId);
            }
            const URL = loadUrl(submissionType);
            const response = await authApi.patch(
                `${URL}/${attributeId}/default`,
                {},
                {
                    params: {
                        company_id: submissionType !== AttributeSubmissionTypes.SHARED ? company_id.value : null,
                    },
                }
            );
            if (response.statusCode === 200) {
                await fetchAllAttributes();
                showToast(response.toast, 'Attribute set as default');
            }
        } catch (error) {
            showToast({}, error?.data?.response ?? 'Failed to set attribute as default');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const transferAttributes = async (attributes, exportFlag) => {
        try {
            EventBus.emit('show-operation-loader');
            // make a loop and export of them
            const total = attributes.length;
            let transferCount = 0;
            for (let i = 0; i < attributes?.length; i++) {
                const attribute = attributes[i];
                // check if tagged attributes are being transferred
                if (attribute.tags && attribute.tags.length) {
                    showToast({ severity: 'warn', summary: 'Error' }, 'Tagged attributes cannot be transferred');
                } else {
                    const transfered = exportFlag ? await exportCompanyToShared(attribute.id) : await importSharedToCompany(attribute.id);
                    // if transfered flag is true then add the count
                    if (transfered) {
                        transferCount++;
                    }
                }
            }
            if (transferCount === total) {
                showToast({ severity: 'success', summary: 'Success' }, `Successfully transfered ${total} attributes`);
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Error while transfering attributes');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const importSharedToCompany = async (attributeId) => {
        try {
            const response = await authApi.patch(
                `${SHARED_ATTRIBUTE_URL}/${attributeId}/import`,
                {},
                {
                    params: {
                        company_id: company_id.value,
                    },
                }
            );
            if (response.statusCode === 201) {
                return true;
            }
            return false;
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to import shared library attributes');
        }
    };

    const exportCompanyToShared = async (attributeId) => {
        try {
            const response = await authApi.patch(`${COMPANY_ATTRIBUTE_URL}/${attributeId}/export`);
            if (response.statusCode === 201) {
                return true;
            }
            return false;
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to export company attribute');
        }
    };

    const loadAttributeFromTagId = (tagId) => {
        try {
            const existingTag = allTags.value?.find((tag) => tag.id === tagId);
            if (existingTag) {
                // load the attribute details inside the tag.attribute
                loadAttributeDetails(existingTag.attribute);
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to load tag data');
        }
    };

    // attribute tags actions
    const fetchAllTags = async () => {
        try {
            const response = await authApi.get(`${COMPANY_ATTRIBUTE_URL}/tags`);
            if (response.statusCode === 200) {
                tags.value = response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch all tags');
        }
    };

    // fetch all tagged attributes which have been shared by the company
    const fetchAllSharedTaggedAttributes = async () => {
        try {
            const response = await authApi.get(SHARED_TAGGED_ATTRIBUTE_URL, {
                params: {
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                sharedTaggedAttributes.value = response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch shared tagged attributes');
        }
    };

    // helpers
    const initAttribute = () => {
        attribute.value.company_id = null;
        attribute.value.label = null;
        attribute.value.code = null;
        attribute.value.description = null;
        attribute.value.placeholder = null;
        attribute.value.icon = null;
        attribute.value.formula_id = null;
        attribute.value.category_id = null;
        attribute.value.type = null;
        attribute.value.required = null;
        // normal/shared attribute properties
        attribute.value.unit = null;
        attribute.value.user_profile = false;
        attribute.value.options = null;
        attribute.value.rules = null;
        // tagged attribute properties
        attribute.value.tags = null;
        attribute.value.shared = null; // shareable flag;
        editId.value = null;
    };

    const loadUrl = (submissionType) => {
        // CASE 1: tagged attribute
        // CASE 2: normal attribute
        return submissionType === AttributeSubmissionTypes.TAGGED ? TAGGED_ATTRIBUTE_URL : COMPANY_ATTRIBUTE_URL;
    };

    const loadAttributeDetails = (attributeData) => {
        attribute.value = {
            company_id: attributeData?.company_id,
            label: attributeData?.label,
            code: attributeData?.code,
            description: attributeData?.description,
            placeholder: attributeData?.placeholder,
            icon: attributeData?.icon,
            formula_id: attributeData?.formula_id,
            category_id: attributeData?.category_id,
            type: parseInt(attributeData?.type),
            required: attributeData?.required === 1 ? true : false,
            // normal/shared attribute properties
            unit: attributeData?.unit,
            user_profile: attributeData?.user_profile === 1 ? true : false,
            options: attributeData?.options,
            rules: attributeData?.rules,
            // tagged attribute properties
            tags: attributeData?.tags ?? [],
            shared: attributeData?.shared, // shareable flag;
        };
    };

    const getSubmissionTypeByAttributeId = (attributeId) => {
        // in case of confirm delete, submission type may not be present
        const existingAttribute = allAttributes.value?.find((attribute) => attribute.id === attributeId);
        if (existingAttribute.tags && existingAttribute.tags.length) {
            return AttributeSubmissionTypes.TAGGED;
        } else {
            return existingAttribute.company_id ? AttributeSubmissionTypes.COMPANY : AttributeSubmissionTypes.SHARED;
        }
    };

    const clearFilters = () => {
        params.value.code = null;
        params.value.shared = SharedTypes.COMPANY;
        params.value.default = true;
        params.value.user_profile = UserProfileTypes.NORMAL;
    };

    return {
        loading,
        // utils
        input_types,
        // states
        params,
        attribute,
        editId,
        attributes,
        totalAttributes,
        // tags,
        // sharedTaggedAttributes,
        // getters
        allAttributes,
        allTags,
        allSharedTaggedAttributes,
        // actions
        fetchAllAttributes,
        createAttribute,
        editAttribute,
        updateAttribute,
        deleteAttribute,
        setDefaultAttribute,
        transferAttributes,
        fetchAllTags,
        fetchAllSharedTaggedAttributes,
        loadAttributeFromTagId,
        // helpers
        initAttribute,
        clearFilters,
    };
});
